import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import "./Meetingtimer.css";

/**
 * MeetingTimer Component
 * - Displays meeting timing in the room
 * - Shows countdown/remaining time
 * - Automatically ends meeting when time expires
 * - Shows warnings before meeting ends
 * 
 * Usage:
 * <MeetingTimer onMeetingEnd={handleMeetingEnd} />
 */
const MeetingTimer = ({ onMeetingEnd }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [meetingStatus, setMeetingStatus] = useState('active'); // 'active', 'warning', 'ending', 'ended'
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef(null);
  const warningShownRef = useRef(false);
  const fiveMinWarningShownRef = useRef(false);

  useEffect(() => {
    // Extract timing from URL
    const urlParams = new URLSearchParams(location.search);
    const fromTime = urlParams.get('fromTime');
    const toTime = urlParams.get('toTime');
    const date = urlParams.get('date');

    console.log('🔍 MeetingTimer - Checking URL parameters:');
    console.log('  fromTime:', fromTime);
    console.log('  toTime:', toTime);
    console.log('  date:', date);

    if (fromTime && toTime && date) {
      // Parse the meeting time
      const endDateTime = new Date(`${date}T${toTime}:00`);
      const startDateTime = new Date(`${date}T${fromTime}:00`);
      
      setMeetingInfo({
        fromTime,
        toTime,
        date,
        endDateTime,
        startDateTime
      });

      console.log('✅ Meeting timer initialized');
      console.log('📅 Meeting scheduled:', fromTime, '-', toTime);
      console.log('⏰ Meeting ends at:', endDateTime.toLocaleString());
      console.log('🕐 Current time:', new Date().toLocaleString());
    } else {
      console.log('⚠️ Meeting timer not initialized - missing timing parameters');
      console.log('   Make sure URL has: ?fromTime=HH:mm&toTime=HH:mm&date=YYYY-MM-DD');
    }
  }, [location.search]);

  useEffect(() => {
    if (!meetingInfo) {
      console.log('⏭️ No meeting info, timer not started');
      return;
    }

    console.log('▶️ Starting meeting timer interval...');

    // Update time remaining every second
    timerRef.current = setInterval(() => {
      const now = new Date();
      const remaining = meetingInfo.endDateTime - now;

      setTimeRemaining(remaining);

      // Check if meeting has ended
      if (remaining <= 0) {
        console.log('⏱️ Time is up! Meeting should end now.');
        handleMeetingEnd();
      } 
      // Show warning 5 minutes before end
      else if (remaining <= 5 * 60 * 1000 && !fiveMinWarningShownRef.current) {
        console.log('⚠️ 5 minutes remaining - showing warning');
        setMeetingStatus('warning');
        setShowWarning(true);
        fiveMinWarningShownRef.current = true;
        
        // Hide warning after 10 seconds
        setTimeout(() => setShowWarning(false), 10000);
      }
      // Show critical warning 1 minute before end
      else if (remaining <= 60 * 1000 && !warningShownRef.current) {
        console.log('🚨 1 minute remaining - critical warning');
        setMeetingStatus('ending');
        warningShownRef.current = true;
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        console.log('⏹️ Stopping meeting timer interval');
        clearInterval(timerRef.current);
      }
    };
  }, [meetingInfo]);

const handleMeetingEnd = () => {
  console.log('🔚 handleMeetingEnd called');
  setMeetingStatus('ended');
  
  // Clear the timer
  if (timerRef.current) {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }

  // Call the callback if provided
  if (onMeetingEnd) {
    console.log('📞 Calling onMeetingEnd callback');
    onMeetingEnd();
    setTimeout(() => {
      window.close();
    }, 3000);
  } else {
    console.log('⚠️ No onMeetingEnd callback provided, closing tab');
    setTimeout(() => {
      window.close();
    }, 3000);
  }
};

  // Converts "HH:mm" (24hr) to "h:mm AM/PM" (12hr)
  const formatTime12hr = (time24) => {
    if (!time24) return '';
    const [hourStr, minute] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // Convert 0 → 12 for midnight
    return `${hour}:${minute} ${ampm}`;
  };

  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return 'Meeting Ended';

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s remaining`;
    } else {
      return `${seconds}s remaining`;
    }
  };

  // Don't render if no timing info
  if (!meetingInfo) {
    console.log('🚫 MeetingTimer not rendering - no meeting info');
    return null;
  }

  console.log('✅ MeetingTimer rendering with status:', meetingStatus);

  return (
    <>
      {/* Meeting Timer Display */}
      <div className={`meeting-timer ${meetingStatus}`}>
        <div className="timer-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
          </svg>
        </div>
        <div className="timer-info">
          <div className="timer-schedule">
            {formatTime12hr(meetingInfo.fromTime)} - {formatTime12hr(meetingInfo.toTime)}
          </div>
          <div className="timer-remaining">
            {formatTimeRemaining(timeRemaining)}
          </div>
        </div>
      </div>

      {/* Warning Modal */}
      {showWarning && (
        <div className="meeting-warning-modal">
          <div className="warning-content">
            <div className="warning-icon">⚠️</div>
            <h3>Meeting Ending Soon</h3>
            <p>This meeting will end in 5 minutes.</p>
          </div>
        </div>
      )}

      {/* Meeting Ended Overlay */}
      {meetingStatus === 'ended' && (
        <div className="meeting-ended-overlay">
          <div className="ended-content">
            <div className="ended-icon">⏰</div>
            <h2>Meeting Time Has Ended</h2>
            <p>Thank you for joining. Redirecting you now...</p>
          </div>
        </div>
      )}
    </>
  );
};

export default MeetingTimer;